import React, { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import './App.css';
import Home from './pages/home';
import SpellingAssistant from './pages/spellingassistant';
import DragDrop from './pages/dragdrop';
import MissingLetters from './pages/missingletters';
import ChatTutor from './pages/ChatTutor';
import { AuthProvider } from './contexts/AuthContext';
import Phoneme from './pages/phoneme';
import SentenceFormation from './pages/sentenceformation';
import MathActivities from './pages/mathactivities';
import Dashboard from './pages/Dashboard';

const Level1 = lazy(() => import('./pages/Level1'));
const Level2 = lazy(() => import('./pages/Level2'));
const Level3 = lazy(() => import('./pages/Level3'));
const Level4 = lazy(() => import('./pages/Level4'));
const Level5 = lazy(() => import('./pages/Level5'));
const Level6 = lazy(() => import('./pages/Level6'));

const LEVEL_COMPONENTS = { 1: Level1, 2: Level2, 3: Level3, 4: Level4, 5: Level5, 6: Level6 };

const KNOWN_PAGES = [
  'home', 'spellingassistant', 'dragdrop', 'missingletters', 'chattutor',
  'phoneme', 'sentenceformation', 'mathactivities', 'dashboard',
  'level1', 'level2', 'level3', 'level4', 'level5', 'level6',
];

function pageFromHash() {
  const hash = window.location.hash.replace('#', '');
  return KNOWN_PAGES.includes(hash) ? hash : 'home';
}

function App() {
  const [currentPage, setCurrentPage] = useState(pageFromHash);

  const handleNavigate = useCallback((page) => {
    window.history.pushState({ page }, '', `#${page}`);
    setCurrentPage(page);
  }, []);

  useEffect(() => {
    // Replace the initial history entry so the very first back-press works correctly
    window.history.replaceState({ page: currentPage }, '', `#${currentPage}`);

    const onPopState = (e) => {
      const page = e.state?.page ?? pageFromHash();
      setCurrentPage(page);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderLevel = () => {
    const match = currentPage.match(/^level(\d)$/);
    if (!match) return null;
    const LevelComponent = LEVEL_COMPONENTS[Number(match[1])];
    return LevelComponent ? (
      <Suspense fallback={<div style={{ textAlign: 'center', padding: '2rem' }}>Loading…</div>}>
        <LevelComponent onNavigate={handleNavigate} />
      </Suspense>
    ) : null;
  };

  return (
    <AuthProvider>
      <div className="App">
        {currentPage === 'home' && <Home onNavigate={handleNavigate} />}
        {currentPage === 'spellingassistant' && <SpellingAssistant onNavigate={handleNavigate} />}
        {currentPage === 'dragdrop' && <DragDrop onNavigate={handleNavigate} />}
        {currentPage === 'missingletters' && <MissingLetters onNavigate={handleNavigate} />}
        {currentPage === 'chattutor' && <ChatTutor onNavigate={handleNavigate} />}
        {currentPage === 'phoneme' && <Phoneme onNavigate={handleNavigate} />}
        {currentPage === 'sentenceformation' && <SentenceFormation onNavigate={handleNavigate} />}
        {currentPage === 'mathactivities' && <MathActivities onNavigate={handleNavigate} />}
        {currentPage === 'dashboard' && <Dashboard onNavigate={handleNavigate} />}
        {renderLevel()}
      </div>
    </AuthProvider>
  );
}

export default App;
